import React, { useState, useRef, useEffect } from 'react';
import { Menu, Bell, User, LogOut, Settings, ChevronDown } from 'lucide-react';
import { useUser } from '../../contexts/UserContext';

interface HeaderProps {
  onMenuClick: () => void;
  isMobile: boolean;
}

export const Header: React.FC<HeaderProps> = ({ onMenuClick, isMobile }) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { email, loading } = useUser();

  // Derive display name from email (part before @)
  const displayName = email ? email.split('@')[0] : 'User';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = () => {
    // Redirect to Cloudflare Access logout, then back to app
    const logoutUrl = `https://${window.location.hostname}/cdn-cgi/access/logout`;
    window.location.href = logoutUrl;
  };

  return (
    <header className="bg-white border-b border-gray-200 shadow-sm" data-testid="header">
      <div className="flex items-center justify-between h-16 px-4">
        {/* Left side - Menu button (mobile) and Title */}
        <div className="flex items-center">
          {isMobile && (
            <button
              onClick={onMenuClick}
              className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 mr-3"
              data-testid="mobile-menu-button"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          )}
          <div>
            <h1 className="text-lg font-semibold text-gray-900" data-testid="header-title">
              Medicaid Contract Analysis
            </h1>
            <p className="text-xs text-gray-500 hidden sm:block">
              Pre-RFP Intelligence & Comparative Analysis
            </p>
          </div>
        </div>

        {/* Right side - Notifications and Profile */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <button
            className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 relative"
            data-testid="notifications-button"
            aria-label="View notifications"
          >
            <Bell className="h-5 w-5" />
            {/* Notification badge */}
            <span className="absolute top-1 right-1 h-2 w-2 bg-danger-500 rounded-full"></span>
          </button>

          {/* Profile Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center space-x-2 p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              data-testid="profile-dropdown-button"
              aria-expanded={isProfileOpen}
              aria-haspopup="true"
            >
              <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center">
                <User className="h-5 w-5 text-white" />
              </div>
              <span className="hidden md:block text-sm font-medium">
                {loading ? '...' : displayName}
              </span>
              <ChevronDown className={`hidden md:block h-4 w-4 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown Menu */}
            {isProfileOpen && (
              <div
                className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50"
                data-testid="profile-dropdown-menu"
              >
                <div className="py-1">
                  {/* User Info */}
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">{displayName}</p>
                    <p className="text-xs text-gray-500 truncate">{email || 'Not signed in'}</p>
                  </div>

                  {/* Menu Items */}
                  <button
                    className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    data-testid="settings-button"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    <Settings className="h-4 w-4 mr-3 text-gray-400" />
                    Settings
                  </button>

                  <button
                    className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-100"
                    data-testid="sign-out-button"
                    onClick={handleSignOut}
                  >
                    <LogOut className="h-4 w-4 mr-3 text-gray-400" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
